import { useTranslation } from 'react-i18next';
import { type AnalysisFormState, useAnalysisFormStore } from '../../../model/analysisFormStore';
import { useAnalysisStepValidity } from '../../../model/useAnalysisStepValidity';
import { ModeChoiceStep } from '../shared';
import { CompetitorDefinitionTabs } from './CompetitorDefinitionTabs';
import { CREATE_MANUALLY_TAB, DOCUMENT_EXTRACTION_TAB } from './constants';

/**
 * Letting IQ suggest competitors needs nothing else, so the step is complete on the choice
 * alone. Defining your own is complete once the open tab holds something usable, and not
 * choosing at all leaves it incomplete rather than defaulting to either.
 *
 * The count is checked before the contents because `every` is true of an empty list, which
 * would pass the step on no competitors at all. Nothing reaches that state today, but only
 * because the manual tab hides the remove button on the last row; the store itself has no
 * floor, so the predicate does not lean on a component to keep one.
 */
function computeCompetitorsValid(state: AnalysisFormState): boolean {
  if (state.competitorsMode === 'auto') return true;
  if (state.competitorsMode !== 'manual') return false;
  switch (state.competitorTab) {
    case DOCUMENT_EXTRACTION_TAB:
      return state.competitorUploadedFiles.length > 0;
    case CREATE_MANUALLY_TAB:
      return (
        state.manualCompetitors.length > 0 &&
        state.manualCompetitors.every((c) => c.name.trim() !== '' && c.details.trim() !== '')
      );
    default:
      return false;
  }
}

interface CompetitorsStepProps {
  onValidChange?: (isValid: boolean) => void;
}

export const CompetitorsStep = ({ onValidChange }: CompetitorsStepProps) => {
  const { t } = useTranslation('analysis');
  const mode = useAnalysisFormStore((s) => s.competitorsMode);
  const setMode = useAnalysisFormStore((s) => s.setCompetitorsMode);

  useAnalysisStepValidity(computeCompetitorsValid, onValidChange);

  return (
    <ModeChoiceStep
      title={t('analysis.competitorsStepV2.competitors')}
      subtitle={t('analysis.competitorsStepV2.whichBrandsToBenchmark')}
      autoLabel={t('analysis.competitorsStepV2.letIqSuggestCompetitors')}
      autoDescription={t('analysis.competitorsStepV2.iqRunsDeepResearch')}
      manualLabel={t('analysis.competitorsStepV2.defineMyOwnCompetitors')}
      manualDescription={t('analysis.competitorsStepV2.addTheExactSet')}
      value={mode}
      onChange={setMode}
    >
      {mode === 'manual' && <CompetitorDefinitionTabs />}
    </ModeChoiceStep>
  );
};
